import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nameof } from 'utils/object';
import TailwindMultiSelect from '../../editors/tailwind-multiselect';
import { connect } from 'redux-scaffolding-ts';
import { ProfessionsStore, ProfessionDto } from 'stores/configuration/profiles/profession-roles-store';

interface MultipleProfessionsEditorProps {
  value: ProfessionDto[];
  onChange?: (value: ProfessionDto[]) => void;
  professions?: ProfessionsStore;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  loadDataOnOpen?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

const TailwindMultipleProfessionsEditor: React.FC<MultipleProfessionsEditorProps> = ({
  value,
  onChange,
  professions,
  placeholder,
  clearable = true,
  disabled = false,
  className,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<ProfessionDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getProfessionsMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await professions.getAllAsync({
        searchQuery: '',
        skip: 0,
        take: 100000,
        orderBy: [{ direction: 'Ascending', field: nameof<ProfessionDto>('profession'), useProfile: false }],
        filter: []
      });

      setOptions(result.items || []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [professions]);

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getProfessionsMethod();
    }
  }, [loadDataOnOpen, options.length, getProfessionsMethod]);

  useEffect(() => {
    if (!loadDataOnOpen) {
      getProfessionsMethod();
    }
  }, [loadDataOnOpen, getProfessionsMethod]);

  return (
    <TailwindMultiSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(profession => ({
              id: profession.id,
              text: profession.profession
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={value?.map(v => ({
        id: v.id,
        text: v.profession
      }))}
      onChange={newValue => {
        const selectedProfessions = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
        onChange(selectedProfessions);
      }}
      placeholder={placeholder || t('Select roles')}
      clearable={clearable}
      className={className}
      onBlur={onBlur}
      onFocus={onFocus}
      searchable={!isLoading}
      disabled={disabled}
      onOpenDropDown={onOpenDropDown}
    />
  );
};

export default connect(['professions', ProfessionsStore])(TailwindMultipleProfessionsEditor);
